import { createSelector } from 'reselect';
import { PapiGallection } from '@wix/da-types-papi';
import { AppState } from '../../types/store';
import {
  galleryFolderSchema,
  collectionFolderSchema,
} from '@wix/da-shared-react/pkg/redux/normalizr/schemas/folder';
import { getDenormalizedDataEntityById } from '@wix/da-shared-react/pkg/redux/entities';
import { GallectionType } from '@wix/da-shared-react/pkg/types/gallection';
import { ALL_FOLDER_ID } from '@wix/da-url';
import { getGallectionSectionType } from './gallectionSection';
import { getDefaultFolderFromFolders } from '../components/Sections/GallectionsSection/helpers';
import { putScrapsFolderLast } from '../utils/folders';

const DEFAULT_FOLDERS = [ALL_FOLDER_ID];

export const getFolderById = (
  state: AppState,
  gallectionType: GallectionType,
  id: number
): PapiGallection =>
  getDenormalizedDataEntityById(
    state,
    gallectionType === 'gallery' ? galleryFolderSchema : collectionFolderSchema,
    id
  );

export const getFoldersByIds = (
  state: AppState,
  gallectionType: GallectionType,
  ids
): PapiGallection[] => ids.map(id => getFolderById(state, gallectionType, id));

function isFolderNotEmpty(folder: PapiGallection, folders: PapiGallection[]) {
  return (
    // has deviations
    folder.size > 0 ||
    // always needs to be there and/or shown
    DEFAULT_FOLDERS.includes(folder.folderId) ||
    // has subfolders that have contents
    folders.some(f => f.parentId === folder.folderId && f.size > 0)
  );
}

/** Return only folders have deviations, subfolders, or are a default (like 'all') */
export const filterOutEmpty = (folders: PapiGallection[]): PapiGallection[] =>
  folders.filter(folder => isFolderNotEmpty(folder, folders));

function filterOutEmptyAndSubfolders(folders: PapiGallection[]) {
  return folders.filter(
    folder => !folder.parentId && isFolderNotEmpty(folder, folders)
  );
}

export const getFoldersByType = (state: AppState) => {
  const folderType = getGallectionSectionType(state);
  return getFoldersByGallectionType(state, folderType);
};

export const getNonEmptyFolders = createSelector([getFoldersByType], folders =>
  filterOutEmpty(folders)
);

function orderFolders(folders: PapiGallection[]) {
  return folders
    .map((folder, index) => ({ ...folder, order: index }))
    .sort(putScrapsFolderLast)
    .map(({ order, ...folder }) => folder as PapiGallection);
}

export const getOrderedNonEmptyFolders = createSelector(
  [getNonEmptyFolders],
  orderFolders
);

export const getOrderedNonEmptyRootFolders = createSelector(
  [getFoldersByType],
  folders => orderFolders(filterOutEmptyAndSubfolders(folders))
);

export const getTotalDeviationsPerFolders = createSelector(
  [getNonEmptyFolders],
  folders =>
    folders.reduce((acc, folder) => {
      const subfolders = folders.filter(f => f.parentId === folder.folderId);
      acc[folder.folderId] =
        folder.size +
        subfolders.reduce(
          (prevValue, subfolder) => prevValue + subfolder.size,
          0
        );
      return acc;
    }, {})
);

export const getDefaultFolder = (state: AppState) =>
  getDefaultFolderFromFolders(getOrderedNonEmptyFolders(state));

export const getFolderIds = state =>
  state.folders[`${getGallectionSectionType(state)}Folders`].ids || [];

export const getFoldersByGallectionType = (
  state: AppState,
  gallectionType: GallectionType
): PapiGallection[] => {
  return getFoldersByIds(
    state,
    gallectionType,
    state.folders[`${gallectionType}Folders`].ids || []
  );
};

export const getSubfolders = (state: AppState, folderId: number | undefined) =>
  getSubfoldersByGallectionType(
    state,
    getGallectionSectionType(state),
    folderId
  );

export const getSubfoldersByGallectionType = (
  state: AppState,
  gallectionType: GallectionType,
  folderId: number | undefined
): PapiGallection[] => {
  return getFoldersByGallectionType(state, gallectionType).filter(
    gallection => gallection.parentId === folderId
  );
};

export const getFeaturedFolder = createSelector(
  [getFoldersByType],
  folders => folders.find(({ name }) => name === 'Featured') || folders[0]
);

export const getAllDeviationsFolder = createSelector(
  [getOrderedNonEmptyFolders],
  folders => folders.find(({ folderId }) => folderId === ALL_FOLDER_ID)
);

export const hasAnyDeviations = (state: AppState): boolean => {
  const allFolder = getAllDeviationsFolder(state);
  const featuredFolder = getFeaturedFolder(state);
  return (
    (!!allFolder && allFolder.size > 0) ||
    (!!featuredFolder && featuredFolder.size > 0)
  );
};
